import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useToast } from 'primevue/usetoast'
import { MdmPolicySchema, PolicySchema } from '@/generated/sdk'
import { merge, formatPolicy, getPoliciesToUpdate } from './policy.utils'
import { usePolicyConfig } from '..'


export default /*#__PURE__*/_defineComponent({
  __name: 'PolicyConfiguration',
  setup(__props) {

const toast = useToast()

const store = useStore()
const router = useRouter()

const mdmPolicies = computed<MdmPolicySchema[]>(() => store.state.policies.mdmPolicies)
const currentMdmPolicy = computed<MdmPolicySchema>(() => store.state.policies.currentPolicy)
const parents = computed(() => getParents(currentMdmPolicy.value).reverse())

const { policyConfig } = usePolicyConfig()

const possibleParentPolicies = computed<MdmPolicySchema[]>(() =>
  mdmPolicies.value.filter(
    mdmPolicy =>
      mdmPolicy.id !== currentMdmPolicy.value?.id &&
      !getParents(mdmPolicy).find(parent => parent.id === currentMdmPolicy.value?.id),
  ),
)

const parentId = ref<string>(currentMdmPolicy.value?.parent?.id || '')

watch(currentMdmPolicy, () => {
  parentId.value = currentMdmPolicy.value?.parent?.id
})

function getParents(mdmPolicy: MdmPolicySchema): MdmPolicySchema[] {
  const parent = getParent(mdmPolicy)
  return parent ? [parent].concat(getParents(parent)) : []
}

function getParent(mdmPolicy: MdmPolicySchema): MdmPolicySchema {
  return mdmPolicies.value.find(m => m.id === mdmPolicy?.parent?.id)
}

async function tryUpdatePolicy() {
  try {
    await updatePolicy()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Update failed',
      detail: 'Something went wrong while updating the policies',
      life: 3000,
    })
  }
}

async function updatePolicy() {
  // Get the new config and connect it to the cloned policy
  const newConfigPolicy: PolicySchema = JSON.parse(formatPolicy(policyConfig.value))

  const clonedMdmPolicies: MdmPolicySchema[] = JSON.parse(JSON.stringify(mdmPolicies.value))
  const clonedMdmPolicy = clonedMdmPolicies.find(p => p.id === currentMdmPolicy.value.id)

  clonedMdmPolicy.configPolicy = newConfigPolicy

  clonedMdmPolicy.parent = parentId.value ? ({ id: parentId.value } as MdmPolicySchema) : undefined

  // Re-generate all policies with the new config
  for (const mdmPolicy of clonedMdmPolicies) {
    mdmPolicy.policy = merge(mdmPolicy, clonedMdmPolicies)
  }

  // Get all policies to update
  const policiesToUpdate = await getPoliciesToUpdate(
    mdmPolicies.value.map(p => p.policy),
    clonedMdmPolicies.map(p => p.policy),
  )

  if (policiesToUpdate.length === 0) {
    alert('No policies to update')
  } else {
    if (confirm(`Update ${policiesToUpdate.length} policies? (This will only succeed if the config policy is valid)`)) {
      // First update our policy (may still fail validation)
      await store.dispatch('updatePolicies', [clonedMdmPolicy.configPolicy].concat(policiesToUpdate))
    }
  }

  // Always update the parent
  await store.dispatch('updateMdmPolicy', clonedMdmPolicy)
  router.go(0)
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_DodoChip = _resolveComponent("DodoChip")!
  const _component_DodoIcon = _resolveComponent("DodoIcon")!
  const _component_DodoRow = _resolveComponent("DodoRow")!
  const _component_PrimeDropdown = _resolveComponent("PrimeDropdown")!
  const _component_DodoButton = _resolveComponent("DodoButton")!
  const _component_DodoColumn = _resolveComponent("DodoColumn")!
  const _component_PrimePanel = _resolveComponent("PrimePanel")!
  const _component_PolicyConfigurationSettings = _resolveComponent("PolicyConfigurationSettings")!
  const _component_DodoCard = _resolveComponent("DodoCard")!
  const _component_PlayStore = _resolveComponent("PlayStore")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_PrimePanel, {
      header: "Parent policies",
      toggleable: "",
      class: "panel"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_DodoColumn, null, {
          default: _withCtx(() => [
            (parents.value.length)
              ? (_openBlock(), _createBlock(_component_DodoRow, {
                  key: 0,
                  gap: "0"
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(parents.value, (parent, index) => {
                      return (_openBlock(), _createElementBlock(_Fragment, {
                        key: parent.id
                      }, [
                        _createVNode(_component_DodoChip, {
                          color: index === parents.value.length - 1 ? 'success' : 'foreground'
                        }, {
                          default: _withCtx(() => [
                            (index == 0)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _createTextVNode("Base: ")
                                ], 64))
                              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                  _createTextVNode("Parent: ")
                                ], 64)),
                            _createVNode(_component_router_link, {
                              to: `/policies/${parent.id}`
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(parent.name), 1)
                              ]),
                              _: 2
                            }, 1032, ["to"])
                          ]),
                          _: 2
                        }, 1032, ["color"]),
                        (index < parents.value.length - 1)
                          ? (_openBlock(), _createBlock(_component_DodoIcon, {
                              key: 0,
                              name: "chevron_right"
                            }))
                          : _createCommentVNode("", true)
                      ], 64))
                    }), 128))
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            _createVNode(_component_DodoRow, null, {
              default: _withCtx(() => [
                _createVNode(_component_PrimeDropdown, {
                  modelValue: parentId.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((parentId).value = $event)),
                  class: "dropdown",
                  options: possibleParentPolicies.value,
                  loading: possibleParentPolicies.value == null,
                  "show-clear": true,
                  "option-value": "id",
                  "option-label": "name",
                  placeholder: "Select a parent policy"
                }, null, 8, ["modelValue", "options", "loading"]),
                _createVNode(_component_DodoButton, {
                  variant: "solid",
                  color: "success",
                  onClick: updatePolicy
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_DodoIcon, { name: "save" }),
                    _createTextVNode(" Save ")
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(_component_DodoCard, null, {
      default: _withCtx(() => [
        _createVNode(_component_PolicyConfigurationSettings),
        _createVNode(_component_DodoRow, { justify: "end" }, {
          default: _withCtx(() => [
            _createVNode(_component_DodoButton, {
              variant: "solid",
              color: "success",
              onClick: tryUpdatePolicy
            }, {
              default: _withCtx(() => [
                _createVNode(_component_DodoIcon, { name: "save" }),
                _createTextVNode(" Save configuration ")
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(_component_PrimePanel, {
      header: "Managed configuration (BETA)",
      toggleable: "",
      collapsed: "",
      class: "panel"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_PlayStore)
      ]),
      _: 1
    })
  ], 64))
}
}

})